<template>
  <div :style="cssVars">
    <infinite-scroll-detector
      use-first-child
      @load-more="onLoadMoreViaScroll"
    >
      <div
        class="base-screen-tab"
        :class="{ filtering: filteringApplied }"
      >
        <!-- List View -->
        <base-screen-tab-list-view
          v-if="showListView"
          :search-input.sync="searchInputModel"
          :result-count="searchResultCount"
          :loading="loading"
        >
          <!-- Slot for custom list filtering components -->
          <template #filters>
            <slot name="filters"></slot>
          </template>

          <!-- List of screen cards -->
          <template #screen-cards>
            <!-- Render each reference as a card -->
            <template v-for="reference in referencesPendingScreening">
              <!-- Slot for custom screen card components -->
              <slot
                name="screen-card"
                v-bind="{
                  // Props to be passed to the slot
                  reference,
                  shouldShowReference: shouldShowReference(reference),
                  hide: triggerCollapseAnimation(reference),
                  animateOpen: wasUndone(reference),
                  searchHighlights: searchHighlights(reference),
                  // Event handlers to be passed to the slot
                  onOpen: () => (selectedReferenceModel = reference),
                  onEditField: (field) =>
                    onEditReferenceField(reference, field),
                  onSavingVote: onSavingVoteInListView,
                  onSavedVote: storeVoteInCache,
                  onSavingOutcome: onSavingVoteInListView,
                  onSavedOutcome: storeVoteInCache,
                  onRevokeOutcome: () => onUndoVote(reference),
                  onHidden: () => hideReference(reference),
                  onAnimatedOpen: clearLastUndoneReference,
                }"
              ></slot>
            </template>

            <!-- Loading more indicator + search results empty state -->
            <template v-if="!loading">
              <loading-more-indicator
                v-if="!showEmptySearchResults"
                :loading="loadingMore"
                :has-next-page="hasMore"
                @load-more="onLoadMore"
              />
              <div v-if="showEmptySearchResults">
                No results found for "{{ searchInput }}". Try another
                phrase or
                <a
                  class="font-weight-bold text-decoration-none"
                  @click="searchInputModel = null"
                >
                  clear your search</a
                >.
              </div>
            </template>
          </template>
        </base-screen-tab-list-view>

        <!-- Focus View (shown instead when reference is selected) -->
        <base-screen-tab-focus-view
          v-else-if="selectedReference"
          :review="review"
          :selected-reference="selectedReference"
          :current-researcher="currentResearcher"
          :loading-more="showLoadingStateInFocusView"
          :show-undo-button="showUndoButton"
          :disable-previous-button="disablePreviousButton"
          :disable-next-button="disableNextButton"
          :highlights="searchHighlights(selectedReference)"
          :filtering-applied="filteringApplied"
          :show-full-text="showFullText"
          @show-list="selectedReferenceModel = null"
          @nav-next="selectNextReferenceOrLoadMore"
          @nav-previous="selectPreviousReferenceOrLoadMore"
          @undo="onUndoVote"
          @edit-field="
            onEditReferenceField(selectedReference, $event)
          "
          @update-tags="onSetReferenceTags"
        >
          <template #actions>
            <!-- Slot for custom focus actions component -->
            <slot
              name="focus-actions"
              v-bind="{
                onSavingVote: onSavingVoteInFocusView,
                onSavedVote: storeVoteInCache,
                onSavingOutcome: onSavingVoteInFocusView,
                onSavedOutcome: storeVoteInCache,
                onMarkedDuplicate: onMarkedDuplicate,
              }"
            >
              Focus Actions slot
            </slot>
          </template>
        </base-screen-tab-focus-view>

        <!-- Empty state -->
        <template v-else>
          <!-- Empty state shown when no references after filtering -->
          <screen-tab-filtering-empty-state
            v-if="filteringInput.hasFiltersApplied()"
            @clear="$emit('clear-filtering-input')"
          />

          <!-- Slot for custom empty state when no references without filtering -->
          <slot v-else name="empty-state"></slot>
        </template>
      </div>
    </infinite-scroll-detector>

    <!-- Duplicate merge bar — shown in both list and focus view -->
    <undo-bar
      v-if="lastDuplicateMerge"
      :publication="{ title: lastDuplicateMerge.referenceTitle }"
      :merged-into-title="lastDuplicateMerge.originalTitle"
      @undo="onUndoLastDuplicateMerge"
      @view-duplicates="onViewDuplicates"
      @dismiss="lastDuplicateMerge = null"
    />

    <!-- Vote undo bar — list view only; focus view has its own undo button -->
    <undo-bar
      v-else-if="!selectedReference && lastVote"
      :publication="lastVote.reference.publication"
      :vote="lastVote.vote || lastVote.outcome"
      @undo="onUndoLastVote"
    />

    <!-- Reference form modal - allows editing fields of references -->
    <reference-form-modal
      :reference="editReference"
      :focus-field="editField"
      @save="onSaveReference"
      @cancel="editReference = null"
    />

    <!-- Shown when a reference's full details fail to load -->
    <v-snackbar
      :value="!!detailsLoadFailedReference"
      color="error"
      :timeout="-1"
      @input="detailsLoadFailedReference = null"
    >
      Could not load this reference's full details — comments, history
      and PDF status may be missing.
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="onRetryReferenceDetails">
          Retry
        </v-btn>
        <v-btn
          icon
          v-bind="attrs"
          @click="detailsLoadFailedReference = null"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import InfiniteScrollDetector from '@/components/Common/InfiniteScrollDetector';
import LoadingMoreIndicator from '@/components/Common/LoadingMoreIndicator';
import ReferenceFormModal from '@/components/ReferenceForm/ReferenceFormModal';
import BaseScreenTabFocusView from './BaseScreenTabFocusView';
import BaseScreenTabListView from './BaseScreenTabListView';
import ScreenTabFilteringEmptyState from './ScreenTabFilteringEmptyState';
import UndoBar from './UndoBar';

import {
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import { Review, Reference, Researcher } from '@/models';
import { GetReferenceDetails } from '@/graphql';
import { mapActions } from 'vuex';
import { debounce } from 'lodash';
import { appendNextOffsetPageResults } from '@/modules/paginationHelpers';
import {
  selectAdjacentItem,
  SelectDirection,
} from './utils/selectionUtils';

export default {
  name: 'BaseScreenTab',
  components: {
    InfiniteScrollDetector,
    LoadingMoreIndicator,
    BaseScreenTabFocusView,
    BaseScreenTabListView,
    ReferenceFormModal,
    ScreenTabFilteringEmptyState,
    UndoBar,
  },
  props: {
    // Selected reference model (synced with parent)
    selectedReference: {
      type: Reference,
      default: null,
    },
    // Review & current user props
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    // GraphQL Connections
    pendingConnection: {
      type: ReferenceOffsetConnection,
      default: null,
    },
    historyConnection: {
      type: ReferenceOffsetConnection,
      default: null,
    },
    pendingConnectionQuery: {
      type: Object,
      required: true,
    },
    historyConnectionQuery: {
      type: Object,
      default: null,
    },
    // Filtering models
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    searchInput: {
      type: String,
      default: null,
    },
    // Misc props
    disableSelectNextOnVote: {
      type: Boolean,
      default: false,
    },
    hideOnUndo: {
      type: Boolean,
      default: false,
    },
    undoBarHeight: {
      type: Number,
      default: 53,
    },
    showFullText: {
      type: Boolean,
      default: false,
    },
    // Functions
    getVoteByCurrentUserFunc: {
      type: Function,
      default: () => undefined,
    },
    revokeVoteFunc: {
      type: Function,
      default: () => undefined,
    },
    shouldHideReferenceFunc: {
      type: Function,
      default: () => true,
    },
    shouldHideReferenceBasedOnVoteFunc: {
      type: Function,
      default: () => true,
    },
    canUndoVoteOnReferenceFunc: {
      type: Function,
      default: () => true,
    },
  },
  emits: [
    'update:pendingConnection',
    'update:selectedReference',
    'update:searchInput',
    'saving-vote',
    'clear-filtering-input',
  ],
  data: () => ({
    editReference: null,
    editField: null,
    referenceVoteCacheMap: {},
    lastVote: null,
    lastDuplicateMerge: null,
    lastUndoneReference: null,
    referenceHiddenMap: {},
    referenceDetailsCache: {},
    detailsLoadFailedReference: null,
    showLoadingStateInFocusView: false,
    loadMoreDebounced: () => {},
    loadMoreHistoryDebounced: () => {},
  }),
  computed: {
    selectedReferenceModel: {
      get: function () {
        return this.selectedReference;
      },
      set: function (value) {
        this.$emit('update:selectedReference', value);
      },
    },
    searchInputModel: {
      get: function () {
        return this.searchInput;
      },
      set: function (value) {
        this.$emit('update:searchInput', value);
      },
    },
    filteringApplied() {
      return this.filteringInput.hasFiltersApplied();
    },
    searchResultCount() {
      return this.searchInput
        ? this.pendingConnection?.totalCount
        : null;
    },
    loading() {
      return !this.referencesPendingScreening;
    },
    loadingMore() {
      return (
        this.referencesPendingScreening !== null &&
        this.pendingConnectionQuery?.loading
      );
    },
    loadingMoreHistory() {
      return (
        this.referencesByScreeningVoteHistory !== null &&
        this.historyConnectionQuery?.loading
      );
    },
    hasReferences() {
      if (!this.referencesPendingScreening) return false;
      return (
        this.referencesPendingScreening.length -
          this.referencesHiddenCount >
        0
      );
    },
    hasMore() {
      return this.pendingConnection?.hasNextPage;
    },
    hasMoreHistory() {
      return this.historyConnection?.hasNextPage;
    },
    referencesWithVoteCount() {
      return this.referencesPendingScreening.filter(
        (ref) => this.referenceVoteCacheMap[ref.id]
      ).length;
    },
    referencesHiddenCount() {
      // Number of references that have been marked as hidden
      // and still exist in the pending list.
      return Object.keys(this.referenceHiddenMap).filter((refId) =>
        this.referencesPendingScreening.find(
          (ref) => ref.id === refId
        )
      ).length;
    },
    selectedReferenceIndex() {
      return this.referencesPendingScreening?.findIndex(
        (ref) => ref.id === this.selectedReference?.id
      );
    },
    selectedReferenceHistoryIndex() {
      return this.referencesByScreeningVoteHistory?.findIndex(
        (ref) => ref.id === this.selectedReference?.id
      );
    },
    referencesPendingScreening() {
      return this.pendingConnection?.nodes ?? null;
    },
    referencesByScreeningVoteHistory() {
      return this.historyConnection?.nodes ?? null;
    },
    disablePreviousButton() {
      const historyLength =
        this.referencesByScreeningVoteHistory?.length || 0;

      const firstInPendingAndNoHistory =
        this.selectedReferenceIndex === 0 && historyLength === 0;

      const lastInHistoryAndNoMoreHistory =
        this.selectedReferenceHistoryIndex === historyLength - 1 &&
        this.selectedReferenceHistoryIndex >= 0 &&
        !this.hasMoreHistory;

      return (
        firstInPendingAndNoHistory || lastInHistoryAndNoMoreHistory
      );
    },
    disableNextButton() {
      if (!this.referencesPendingScreening) return false;
      return (
        this.selectedReferenceIndex ===
          this.referencesPendingScreening.length - 1 && !this.hasMore
      );
    },
    showUndoButton() {
      const hasVoteFromUser = this.referenceHasVoteFromCurrentUser(
        this.selectedReference
      );
      const canUndoVote = this.canUndoVoteOnReferenceFunc(
        this.selectedReference
      );
      return hasVoteFromUser && canUndoVote;
    },
    showEmptySearchResults() {
      return (
        !this.loading && !!this.searchInput && !this.hasReferences
      );
    },
    showListView() {
      return (
        this.loading ||
        (this.hasReferences && !this.selectedReference) ||
        (this.searchInput && !this.selectedReference)
      );
    },
    cssVars() {
      const barShowing =
        !!this.lastDuplicateMerge ||
        (!this.selectedReference && !!this.lastVote);
      return {
        '--undo-bar-height': barShowing
          ? `${this.undoBarHeight}px`
          : '0px',
      };
    },
  },
  watch: {
    pendingConnection(pendingConnection) {
      // Unselect the reference if the pending connection is loading.
      if (!pendingConnection && this.selectedReference) {
        this.selectedReferenceModel = null;
      }
    },
    selectedReference(selectedReference) {
      if (selectedReference) {
        this.ensureReferenceDetailsLoaded(selectedReference);
      }
    },
    referencesPendingScreening(pending) {
      // Fresh list data supersedes any cached detail snapshot — never the
      // other way around, or refetches after mutations show stale data.
      this.invalidateDetailsForFreshList(pending);
      // Ensure the selected reference data is updated when the references
      // change.
      if (this.selectedReferenceIndex >= 0) {
        this.updateSelectedReferenceData(pending);
        this.ensureReferenceDetailsLoaded(this.selectedReference);
      }
    },
    referencesByScreeningVoteHistory(history) {
      this.invalidateDetailsForFreshList(history);
      // Ensure the selected reference data is updated when the references
      // change.
      if (this.selectedReferenceHistoryIndex >= 0) {
        this.updateSelectedReferenceData(history);
        this.ensureReferenceDetailsLoaded(this.selectedReference);
      }
    },
    searchInput() {
      // Reset the pending references connection to null so that the loading
      // state is shown while the query is being executed.
      this.$emit('update:pendingConnection', null);
    },
  },
  mounted() {
    // Debounce the load more methods so that they are only called once
    // per second. This prevents the user from spamming the load more
    // button and causing the server to be overloaded.
    this.loadMoreDebounced = debounce(this.loadMore, 1000, {
      leading: true,
      trailing: true,
    });
    this.loadMoreHistoryDebounced = debounce(
      this.loadMoreHistory,
      1000,
      {
        leading: true,
        trailing: true,
      }
    );
  },
  methods: {
    ...mapActions({
      saveReference: 'reference/updateReferenceFields',
      setReferenceTags: 'reference/setReferenceTags',
    }),
    onSetReferenceTags({ reference, tags }) {
      this.setReferenceTags({
        reference,
        tags,
      });
    },
    replaceReferenceInList(referenceList, replacement) {
      if (!Array.isArray(referenceList)) return;
      const index = referenceList.findIndex(
        (reference) => reference.id === replacement.id
      );
      // Skip the splice when the slot already holds this exact instance:
      // splicing in the same object still fires the list watchers, which
      // re-enter the detail loader and spin an unbounded refetch loop.
      if (index >= 0 && referenceList[index] !== replacement) {
        referenceList.splice(index, 1, replacement);
      }
    },
    invalidateDetailsForFreshList(referenceList) {
      if (!Array.isArray(referenceList)) return;
      referenceList.forEach((reference) => {
        const cached = this.referenceDetailsCache[reference.id];
        // Only drop the cached detail when the list node is a genuinely
        // fresh instance from the server. When it IS our own applied
        // detailed instance (same identity), keeping it stops the loader
        // from perpetually re-invalidating and re-fetching itself.
        if (cached && cached !== reference) {
          this.$delete(this.referenceDetailsCache, reference.id);
        }
      });
    },
    applyDetailedReference(detailedReference) {
      this.replaceReferenceInList(
        this.referencesPendingScreening,
        detailedReference
      );
      this.replaceReferenceInList(
        this.referencesByScreeningVoteHistory,
        detailedReference
      );
      if (this.selectedReference?.id === detailedReference.id) {
        this.selectedReferenceModel = detailedReference;
      }
    },
    async ensureReferenceDetailsLoaded(reference) {
      if (!reference?.id) return;
      this.detailsLoadFailedReference = null;
      const cached = this.referenceDetailsCache[reference.id];
      if (cached) {
        this.applyDetailedReference(cached);
        return;
      }

      try {
        const { data } = await this.$apollo.query({
          query: GetReferenceDetails,
          variables: { referenceId: reference.id },
          fetchPolicy: 'cache-first',
        });
        if (!data?.reference) return;

        const detailedReference = new Reference(data.reference);
        this.$set(
          this.referenceDetailsCache,
          reference.id,
          detailedReference
        );
        this.applyDetailedReference(detailedReference);
      } catch {
        // Keep list interactions usable, but surface that the reference is
        // missing its full details so the user can retry.
        this.detailsLoadFailedReference = reference;
      }
    },
    onRetryReferenceDetails() {
      const reference = this.detailsLoadFailedReference;
      this.detailsLoadFailedReference = null;
      this.ensureReferenceDetailsLoaded(reference);
    },
    onEditReferenceField(reference, field) {
      this.editReference = reference;
      this.editField = field;
    },
    onSaveReference(reference) {
      this.saveReference(reference);
      this.editReference = null;
      this.editField = null;
    },
    onLoadMoreViaScroll() {
      // Only load more on scroll if the list view is showing.
      if (!this.selectedReference) {
        this.onLoadMore();
      }
    },
    onLoadMore() {
      this.loadMoreDebounced();
    },
    referenceHasVoteFromCurrentUser(reference) {
      return !!this.getVoteByCurrentUserFunc(reference);
    },
    updateSelectedReferenceData(updatedReferenceList) {
      const selectedReference = updatedReferenceList.find(
        (ref) => ref.id === this.selectedReference.id
      );
      if (selectedReference) {
        this.selectedReferenceModel = selectedReference;
      }
    },
    shouldShowReference(reference) {
      return !this.referenceHiddenMap[reference.id];
    },
    triggerCollapseAnimation(reference) {
      return (
        this.shouldHideReferenceFunc(reference) &&
        (this.referenceInVoteCache(reference) ||
          this.referenceHasVoteFromCurrentUser(reference) ||
          this.hideOnUndo)
      );
    },
    wasUndone(reference) {
      return reference.id === this.lastUndoneReference?.id;
    },
    searchHighlights(reference) {
      const highlights = this.pendingConnection?.highlights?.find(
        (highlights) => highlights.referenceId === reference?.id
      );
      return highlights?.matches ?? [];
    },
    hideReference(reference) {
      this.$set(this.referenceHiddenMap, reference.id, true);
    },
    showReference(reference) {
      this.$delete(this.referenceHiddenMap, reference.id);
    },
    onSavingVoteInListView(vote) {
      this.$emit('saving-vote', vote);
      this.storeOptimisticVoteInCache(vote);
      this.loadMoreIfNeeded();
    },
    onSavingVoteInFocusView(vote) {
      this.$emit('saving-vote', vote);
      this.storeOptimisticVoteInCache(vote);
      if (this.shouldHideReferenceBasedOnVoteFunc(vote)) {
        this.hideReference(vote.reference);
      } else {
        this.showReference(vote.reference);
      }
      if (!this.disableSelectNextOnVote) {
        this.selectNextReferenceOrLoadMore();
      }
    },
    /**
     * A reference was manually marked as a duplicate. Mirror the vote flow:
     * drop it from view and advance, so the reviewer is not left looking at a
     * reference that no longer exists in the review.
     */
    onMarkedDuplicate(merge) {
      this.lastDuplicateMerge = merge;
      const reference = this.referencesPendingScreening?.find(
        (ref) => ref.id === merge.referenceId
      );
      if (reference) {
        this.hideReference(reference);
      } else {
        this.$set(this.referenceHiddenMap, merge.referenceId, true);
      }
      if (this.selectedReference) {
        this.selectNextReferenceOrLoadMore();
      }
    },
    async onUndoLastDuplicateMerge() {
      const merge = this.lastDuplicateMerge;
      if (!merge) return;

      await this.$store.dispatch('reference/unmarkAsDuplicate', {
        referenceId: merge.referenceId,
      });
      this.$delete(this.referenceHiddenMap, merge.referenceId);
      this.lastDuplicateMerge = null;
    },
    onViewDuplicates() {
      this.$router.push(`/review/${this.review.id}/duplicates`);
    },
    async onUndoVote(targetReference) {
      targetReference = targetReference || this.selectedReference;
      if (!targetReference) return;

      const existingVote = this.getVoteByCurrentUserFunc(
        targetReference
      );
      if (!existingVote)
        throw new Error(
          `No vote to undo for reference ${targetReference.id} for user ${this.currentResearcher.id}`
        );

      const { reference } = await this.revokeVoteAndUpdateCache(
        existingVote,
        targetReference
      );
      if (this.selectedReference) {
        this.selectedReferenceModel = reference;

        // If requested to hide on undo, hide instantly in focus view.
        if (this.hideOnUndo) {
          this.hideReference(reference);
        }
      }
      this.lastVote = null;
    },
    onUndoLastVote() {
      if (!this.lastVote) return;
      this.revokeVoteAndUpdateCache(
        this.lastVote,
        this.lastVote.reference
      );
      if (this.shouldHideReferenceFunc(this.lastVote.reference)) {
        this.lastUndoneReference = this.lastVote.reference;
      }

      // If requested to hide on undo, hide instantly in focus view.
      if (this.selectedReference && this.hideOnUndo) {
        this.hideReference(this.lastVote.reference);
      }

      this.lastVote = null;
    },
    async revokeVoteAndUpdateCache(vote, reference) {
      return await this.revokeVoteFunc({
        id: vote.id,
        reference: reference,
        cacheUpdate: (cache, { reference: latestRef }) => {
          this.removeVoteFromCache(vote);
          this.removeReferenceFromHistoryCache(cache, latestRef);
          this.prependReferenceToPendingCache(cache, latestRef);
          this.showReference(latestRef);
        },
      });
    },
    clearLastUndoneReference() {
      this.lastUndoneReference = null;
    },
    removeReferenceFromHistoryCache(cache, reference) {
      if (!this.historyConnectionQuery) return;
      const {
        query,
        variables,
      } = this.historyConnectionQuery.lastApolloOptions;
      cache.updateQuery(
        {
          query,
          variables,
        },
        (data) => {
          // Return if there is no data.
          if (!data) return;
          const key = Object.keys(data).filter(
            (key) => ['errors', '__typename'].indexOf(key) === -1
          )[0];
          if (!key) throw new Error('No key found in data');
          return {
            [key]: {
              ...data[key],
              nodes: data[key].nodes.filter(
                (ref) => ref.id !== reference.id
              ),
            },
          };
        }
      );
    },
    prependReferenceToPendingCache(cache, reference) {
      const existsInPending = this.referencesPendingScreening.find(
        (ref) => ref.id === reference.id
      );
      if (existsInPending) return;

      const {
        query,
        variables,
      } = this.pendingConnectionQuery.lastApolloOptions;
      cache.updateQuery(
        {
          query,
          variables,
        },
        (data) => {
          const key = Object.keys(data).filter(
            (key) => ['errors', '__typename'].indexOf(key) === -1
          )[0];
          if (!key) throw new Error('No key found in data');
          return {
            [key]: {
              ...data[key],
              nodes: [
                reference,
                ...data[key].nodes.filter(
                  (ref) => ref.id !== reference.id
                ),
              ],
            },
          };
        }
      );
    },
    storeOptimisticVoteInCache(vote) {
      this.storeVoteInCache(vote, false);
    },
    storeVoteInCache(vote, persisted = true) {
      this.$set(this.referenceVoteCacheMap, vote.reference.id, vote);
      if (persisted) {
        this.lastVote = vote;
        // The merge bar takes precedence over the vote bar, so it has to give
        // way once the reviewer has moved on. Without this it would sit there
        // for the rest of this tab's lifetime, permanently suppressing vote
        // undo and offering a one-click undo of a long-forgotten merge.
        // Optimistic writes are excluded: a vote that never persisted must not
        // take the reviewer's undo away.
        this.lastDuplicateMerge = null;
      }
    },
    removeVoteFromCache(vote) {
      this.$delete(this.referenceVoteCacheMap, vote.reference.id);
    },
    referenceInVoteCache(reference) {
      return !!this.referenceVoteCacheMap[reference.id];
    },
    selectPreviousReferenceOrLoadMore() {
      if (!this.selectPreviousReference() && this.hasMoreHistory) {
        this.showLoadingStateInFocusView = true;
      }
      this.loadMoreHistoryIfNeeded();
    },
    selectNextReferenceOrLoadMore() {
      const hasSelectedNext = this.selectNextReference();
      if (!hasSelectedNext && this.hasMore) {
        this.showLoadingStateInFocusView = true;
      } else if (!hasSelectedNext) {
        this.selectedReferenceModel = null;
      }
      this.loadMoreIfNeeded();
    },
    selectNextReference() {
      return this.selectAdjacentReference(SelectDirection.NEXT);
    },
    selectPreviousReference() {
      return this.selectAdjacentReference(SelectDirection.PREVIOUS);
    },
    selectAdjacentReference(direction) {
      const maybeNextReference = selectAdjacentItem(
        direction,
        this.selectedReference,
        this.referencesPendingScreening,
        this.referencesByScreeningVoteHistory
      );
      if (maybeNextReference) {
        this.selectedReferenceModel = maybeNextReference;
      }
      return maybeNextReference;
    },
    loadMoreIfNeeded() {
      const remaining = this.selectedReference
        ? this.referencesPendingScreening.length -
          this.selectedReferenceIndex
        : this.referencesPendingScreening.length -
          this.referencesWithVoteCount;

      if (this.selectedReference && this.selectedReferenceIndex < 0)
        return;

      if (remaining <= 5) {
        this.loadMoreDebounced();
      }
    },
    loadMoreHistoryIfNeeded() {
      if (!this.historyConnectionQuery) return;

      const remaining =
        this.referencesByScreeningVoteHistory.length -
        this.selectedReferenceHistoryIndex;

      if (this.selectedReferenceHistoryIndex >= 0 && remaining <= 5) {
        this.loadMoreHistoryDebounced();
      }
    },
    async loadMore() {
      return this.loadMoreFrom(
        this.loadingMore,
        this.hasMore,
        this.fetchMorePendingAndAppendToCache
      );
    },
    async loadMoreHistory() {
      return this.loadMoreFrom(
        this.loadingMoreHistory,
        this.hasMoreHistory,
        this.fetchMoreHistoryAndAppendToCache
      );
    },
    async loadMoreFrom(loadingMore, hasMore, fetchMoreFunc) {
      if (loadingMore || !hasMore) return;

      const response = await fetchMoreFunc();
      if (this.showLoadingStateInFocusView) {
        const key = Object.keys(response.data).filter(
          (key) => ['errors', '__typename'].indexOf(key) === -1
        )[0];
        if (!key) throw new Error('No key found in data');
        const next = response.data[key].nodes[0];
        if (next) {
          this.selectedReferenceModel = new Reference(next);
        }
        this.showLoadingStateInFocusView = false;
      }
    },
    async fetchMorePendingAndAppendToCache() {
      return this.pendingConnectionQuery.fetchMore({
        variables: {
          offset:
            this.referencesPendingScreening.length -
            this.referencesWithVoteCount,
        },
        updateQuery: appendNextOffsetPageResults({
          removeDuplicates: true,
        }),
      });
    },
    async fetchMoreHistoryAndAppendToCache() {
      return this.historyConnectionQuery.fetchMore({
        variables: {
          offset:
            this.referencesByScreeningVoteHistory.length +
            this.referencesWithVoteCount,
        },
        updateQuery: appendNextOffsetPageResults(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.base-screen-tab {
  --sub-page-tabs-height: 50px;
  --footer-height: 79px;
  --page-header-plus-sub-tabs-height: calc(
    #{$page-header-height} + var(--sub-page-tabs-height)
  );

  &.filtering {
    --filter-bar-height: #{$screen-tab-filtering-status-bar-height};
  }
  &:not(.filtering) {
    --filter-bar-height: 0px;
  }

  --total-footer-height: calc(
    var(--footer-height) + var(--undo-bar-height)
  );
  --max-height: calc(
    100vh - var(--page-header-plus-sub-tabs-height) -
      var(--filter-bar-height) - var(--total-footer-height)
  );
  max-height: var(--max-height);
  height: var(--max-height);
  overflow: auto;
}
</style>
